import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';

const DashboardPage: React.FC = () => {
  const { user, userRole } = useAuth();

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1>Dashboard</h1>
              <p className="text-muted mb-0">
                Welcome back, {user?.name || user?.email}!
              </p>
            </div>
            <div>
              <span className={`badge ${userRole === 'admin' ? 'bg-danger' : 'bg-primary'}`}>
                {userRole?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {userRole === 'admin' ? (
        <AdminDashboard />
      ) : (
        <UserDashboard />
      )}
    </div>
  );
};

const UserDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = React.useState({
    events: 0,
    talks: 0,
    sessions: 0,
    invitations: 0
  });
  const [recentEvents, setRecentEvents] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  const loadDashboardData = React.useCallback(async () => {
    try {
      setLoading(true);

      // Load user's events
      const eventsResponse = await apiClient.get('/v1/events', {
        params: { creator_id: user?.sub }
      });
      const events = eventsResponse.data.data?.events || [];
      setStats(prev => ({ ...prev, events: events.length }));
      setRecentEvents(events.slice(0, 3));

      // Load talks
      try {
        const talksResponse = await apiClient.get('/v1/talks');
        const talks = talksResponse.data.data?.talks || [];
        setStats(prev => ({ ...prev, talks: talks.length }));
      } catch (err) {
        console.error('Failed to load talks:', err);
      }

      // Load sessions
      try {
        const sessionsResponse = await apiClient.get('/v1/sessions');
        const sessions = sessionsResponse.data.data?.sessions || [];
        setStats(prev => ({ ...prev, sessions: sessions.length }));
      } catch (err) {
        console.error('Failed to load sessions:', err);
      }

      // Load invitations
      try {
        const invitationsResponse = await apiClient.get('/v1/invitations');
        const invitations = invitationsResponse.data.data || [];
        const pendingInvitations = invitations.filter((inv: any) => inv.status === 'pending');
        setStats(prev => ({ ...prev, invitations: pendingInvitations.length }));
      } catch (err) {
        console.error('Failed to load invitations:', err);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.sub]);

  React.useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  return (
    <>
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card">
            <div className="card-body text-center">
              <i className="fas fa-calendar fa-2x text-primary mb-3"></i>
              <h5 className="card-title">My Events</h5>
              <h2 className="text-primary">{loading ? '...' : stats.events}</h2>
              <Link to="/events" className="btn btn-outline-primary btn-sm">
                View All
              </Link>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body text-center">
              <i className="fas fa-microphone fa-2x text-success mb-3"></i>
              <h5 className="card-title">My Talks</h5>
              <h2 className="text-success">{loading ? '...' : stats.talks}</h2>
              <Link to="/talks" className="btn btn-outline-success btn-sm">
                View All
              </Link>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body text-center">
              <i className="fas fa-calendar-alt fa-2x text-info mb-3"></i>
              <h5 className="card-title">My Sessions</h5>
              <h2 className="text-info">{loading ? '...' : stats.sessions}</h2>
              <Link to="/sessions" className="btn btn-outline-info btn-sm">
                View All
              </Link>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body text-center">
              <i className="fas fa-envelope fa-2x text-warning mb-3"></i>
              <h5 className="card-title">Invitations</h5>
              <h2 className="text-warning">{loading ? '...' : stats.invitations}</h2>
              <Link to="/invitations" className="btn btn-outline-warning btn-sm">
                View All
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Recent Events</h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : recentEvents.length > 0 ? (
                <div className="list-group list-group-flush">
                  {recentEvents.map((event: any) => (
                    <Link
                      key={event.event_id}
                      to={`/events/${event.event_id}`}
                      className="list-group-item list-group-item-action"
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1">{event.name}</h6>
                          <small className="text-muted">
                            <i className="fas fa-calendar me-1"></i>
                            {new Date(event.start_date).toLocaleDateString()}
                            {' - '}
                            <i className="fas fa-map-marker-alt me-1"></i>
                            {event.location}
                          </small>
                        </div>
                        <span className={`badge ${event.status === 'public' ? 'bg-success' : 'bg-secondary'}`}>
                          {event.status === 'public' ? 'Public' : 'Draft'}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-calendar-times fa-3x mb-3"></i>
                  <p>No events yet.</p>
                  <Link to="/events" className="btn btn-primary">
                    <i className="fas fa-plus me-2"></i>
                    Create Your First Event
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Quick Actions</h5>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                <Link to="/talks/new" className="btn btn-primary">
                  <i className="fas fa-microphone me-2"></i>
                  Create New Talk
                </Link>
                <Link to="/sessions/new" className="btn btn-outline-primary">
                  <i className="fas fa-calendar-alt me-2"></i>
                  Propose Session
                </Link>
                <Link to="/events/public" className="btn btn-outline-secondary">
                  <i className="fas fa-search me-2"></i>
                  Browse Public Events
                </Link>
                <Link to="/users/search" className="btn btn-outline-secondary">
                  <i className="fas fa-users me-2"></i>
                  Find Speakers
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const AdminDashboard: React.FC = () => {
  return (
    <>
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card">
            <div className="card-body text-center">
              <i className="fas fa-users fa-2x text-primary mb-3"></i>
              <h5 className="card-title">Total Users</h5>
              <h2 className="text-primary">0</h2>
              <small className="text-muted">+0 today</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body text-center">
              <i className="fas fa-calendar fa-2x text-success mb-3"></i>
              <h5 className="card-title">Active Events</h5>
              <h2 className="text-success">0</h2>
              <small className="text-muted">+0 today</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body text-center">
              <i className="fas fa-microphone fa-2x text-warning mb-3"></i>
              <h5 className="card-title">Total Sessions</h5>
              <h2 className="text-warning">0</h2>
              <small className="text-muted">+0 today</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body text-center">
              <i className="fas fa-heartbeat fa-2x text-info mb-3"></i>
              <h5 className="card-title">System Health</h5>
              <h2 className="text-info">
                <i className="fas fa-check-circle"></i>
              </h2>
              <small className="text-muted">All systems operational</small>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Recent Admin Activity</h5>
            </div>
            <div className="card-body">
              <div className="text-center text-muted py-4">
                <i className="fas fa-clipboard-list fa-3x mb-3"></i>
                <p>No recent admin activity to display.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Admin Actions</h5>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                <button className="btn btn-danger">
                  <i className="fas fa-users-cog me-2"></i>
                  User Management
                </button>
                <button className="btn btn-outline-danger">
                  <i className="fas fa-cog me-2"></i>
                  System Settings
                </button>
                <button className="btn btn-outline-secondary">
                  <i className="fas fa-chart-bar me-2"></i>
                  View Reports
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
